import classNames from 'classnames';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PauseIcon } from 'tdesign-icons-react';
import { MessagePlugin } from 'tdesign-react';

import { FilePlayIcon } from '@/components/icon';

import { stopPlayer } from '../utils/stopPlay';
import { IPlayer } from '../video/video';
import { Wavesurfer } from '../wavesurfer';
import styles from './audio-play.module.less';

/**
 * 音频播放器
 */
export const AudioPlayer = (props: {
  url: string;
  wrapWidth?: number;
  wrapHeight?: number;
  hiddenPlayBtn?: boolean;
  playRef?: React.ForwardedRef<IPlayer> | null;
  children?: React.ReactNode;
  showWave?: boolean;
  name?: string;
  play?: boolean;
  size?: number;
  getStatus?: (status: boolean) => void;
  disableClick?: boolean; // 禁用内部点击播放，允许外部点击
}) => {
  const { url, wrapWidth, wrapHeight, playRef,
    showWave = false, name, play = true, size = 16,
    getStatus, hiddenPlayBtn = false, disableClick = false,
  } = props;

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const audioRef = useRef<any>(null);

  useImperativeHandle(playRef, () => ({
    triggerPlay: () => handleTrigger(),
    pause: () => pause(),
    play: () => audioRef.current?.play?.(),
    status: () => isPlaying,
  }));

  useEffect(() => () => {
    pause();
  }, []);

  useEffect(() => {
    getStatus?.(isPlaying);
  }, [isPlaying]);

  useEffect(() => {
    if (url) {
      pause();
      stopPlayer();
    }
  }, [url]);

  const handleTrigger = async () => {
    if (!play || !url) {
      return;
    }
    try {
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        await stopPlayer();
        audioRef.current?.play();
      }
      setIsPlaying(pre => !pre);
    } catch (error) {
      MessagePlugin.error('该音频不支持预览');
    }
  };

  const stopPropagation = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!play) {
      return;
    }
    e.stopPropagation();
  };

  const pause = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener('pause', pause);
    }

    return () => {
      if (audio) {
        audio.removeEventListener('pause', pause);
      }
    };
  }, []);

  return (
    <div
      onClick={disableClick ? undefined : stopPropagation}
      className={classNames({
        [styles.audio]: !showWave,
        [styles.center]: !showWave,
      })}
      style={{ display: 'flex', alignItems: 'center', width: wrapWidth, height: wrapHeight }}
    >
      {showWave ? (
        <Wavesurfer url={url} name={name} />
      ) : (
        <>
          <audio ref={audioRef} src={url} preload='none' />
            <div
              style={{ display: 'flex', alignItems: 'center' }}
              onClick={disableClick ? undefined : handleTrigger}
            >
            {props.children}
            {!hiddenPlayBtn && <>
              {isPlaying
                ? <PauseIcon size={size} />
                : <FilePlayIcon style={{ width: size, height: size }} />}
            </>}
          </div>
        </>
      )}
    </div>
  );
};
